import React, { useContext, useState, useEffect } from "react";
import { PageContext } from "../PageContext";
import { Page } from "../types";
import BackButton from "../components/BackButton";
import { useAuth } from "../AuthContext";
import { AdService } from "../AdService";
import { Capacitor } from "@capacitor/core";

const creatorPacksRaw = [
  // Restored 1 Credit pack at £0.30 (Stripe Minimum)
  { amount: 1, icon: "sparkle", title: "1 AI Credit", price: "£0.30" },
  { amount: 5, icon: "lightbulb", title: "5 AI Credits Pack", price: "£0.99" },
  { amount: 15, icon: "gift", title: "15 AI Credits Pack", price: "£1.99" },
  { amount: 30, icon: "crown", title: "30 AI Credits Pack", price: "£4.99" },
  {
    amount: 50,
    icon: "rocket-launch",
    title: "50 AI Credits Pack",
    price: "£7.99",
  },
];

const AIHomePage: React.FC = () => {
  const pageContext = useContext(PageContext);
  const { userProfile, addAICredits, purchaseStoreItem } = useAuth();
  const [purchasingItem, setPurchasingItem] = useState<string | null>(null);
  const [isIOS] = useState(() => Capacitor.getPlatform() === "ios");

  const handleBuy = async (title: string, amount: number, price: string) => {
    setPurchasingItem(title);
    const success = await purchaseStoreItem("credit", amount, price);
    setPurchasingItem(null);

    if (success) {
      alert(`Successfully purchased ${amount} AI Credits!`);
    } else {
      // Success handled by redirect
    }
  };

  return (
    <div className="space-y-6 -mt-4 -mx-4 bg-[var(--primary-dark-purple)] min-h-full p-4 text-white">
      <BackButton colorClass="text-white" />

      <div className="p-6 rounded-2xl text-center bg-black/20 shadow-lg relative overflow-hidden">
        <h1 className="text-4xl font-bold">
          <span className="aurora-text">AI Creator Studio</span>
        </h1>
        <p className="text-white/70 mt-1 font-semibold">
          Supercharge your puzzles with AI!
        </p>
      </div>

      <div className="bg-black/20 p-4 rounded-2xl shadow-lg flex justify-between items-center border border-white/10 opacity-90">
        <p className="font-bold text-white text-lg">Your AI Credit Balance</p>
        <div className="flex items-center gap-2 font-bold text-xl bg-purple-400 text-purple-900 px-4 py-2 rounded-full shadow-md profile-pic-glow">
          <i className="ph-duotone ph-brain"></i>
          <span>{userProfile?.aiCredits ?? 0}</span>
        </div>
      </div>

      <div className="pt-4 space-y-4">
        <h2 className="text-xl font-bold">Tools</h2>

        <div className="bg-black/20 p-4 rounded-2xl shadow-lg flex items-center justify-between space-x-3 border border-white/10">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center shimmer-effect">
              <i
                className={`ph-duotone ph-image-square text-4xl aurora-text`}
              ></i>
            </div>
            <div>
              <h3 className="font-bold text-white text-lg">Generate Image</h3>
              <p className="text-sm text-gray-400">
                Describe an image for your puzzle.
              </p>
            </div>
          </div>
          <button
            onClick={() =>
              pageContext?.switchPage(Page.CreatePost, "Create AI Post", {
                openAI: true,
              })
            }
            className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold text-sm px-5 py-2 rounded-lg shadow-md"
          >
            Create
          </button>
        </div>

        <div>
          <div className="bg-black/20 p-4 rounded-2xl shadow-lg flex items-center justify-between space-x-3 border border-white/10">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center shimmer-effect">
                <i
                  className={`ph-duotone ph-magic-wand text-4xl aurora-text`}
                ></i>
              </div>
              <div>
                <h3 className="font-bold text-white text-lg">
                  Generate Puzzle
                </h3>
                <p className="text-sm text-gray-400">
                  Let AI write a clue for you.
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                pageContext?.switchPage(Page.CreatePost, "Create AI Puzzle")
              }
              className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm px-5 py-2 rounded-lg shadow-md"
            >
              Try It
            </button>
          </div>
        </div>
      </div>

      <div
        onClick={() =>
          pageContext?.switchPage(Page.AICreditStorePage, "Get Credits")
        }
        className={`p-5 rounded-2xl text-center text-white bg-gradient-to-r from-teal-500 to-cyan-600 shadow-lg cursor-pointer transition-transform hover:scale-105`}
      >
        <i className="ph-duotone ph-video-camera text-4xl mb-2"></i>
        <h2 className="text-2xl font-bold">Watch an Ad for a FREE Credit!</h2>
        <p className="text-white/80">Support the app and get a reward.</p>
      </div>

      <div className="space-y-4 pt-4">
        <h2 className="text-xl font-bold">Buy Credits</h2>
        {creatorPacksRaw
          .filter((pack) => !isIOS || pack.amount > 1)
          .map((pack) => (
            <div
              key={pack.title}
              className="bg-black/20 p-4 rounded-2xl shadow-lg flex items-center justify-between space-x-3 border border-white/10 relative overflow-hidden"
            >
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-white/10 rounded-lg flex items-center justify-center shimmer-effect">
                  <i
                    className={`ph-duotone ph-${pack.icon} text-4xl aurora-text`}
                  ></i>
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">{pack.title}</h3>
                  <p className="text-sm text-gray-400">{pack.price}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleBuy(pack.title, pack.amount, pack.price)}
                  disabled={!!purchasingItem}
                  className="bg-gradient-to-r from-pink-500 to-yellow-500 text-white font-bold text-sm px-5 py-2 rounded-lg shadow-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[80px]"
                >
                  {purchasingItem === pack.title ? (
                    <div className="spinner-white h-4 w-4"></div>
                  ) : (
                    "Buy"
                  )}
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default AIHomePage;
